// src/pages/Inventory/STNRequisitionEntry.jsx
// ✅ Converted from: STNrequestion.aspx + STNrequestion.aspx.cs
// ✅ Uses Node routes: /api/inventory/stn-requisition-entry/*
// ✅ NO rights logic (excluded as instructed)
// ✅ FIXED: date field showing raw ISO string → robust toISO() parser
// ✅ FIXED: view mode with existsTransaction=false now shows editable fields + Update button
// ✅ FIXED: dueDate from DB parsed correctly for <input type="date">
// ✅ All business logic preserved

import { useEffect, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../../components/HeaderBar";
import LayoutWrapper from "../../../components/LayoutWrapper";
import getCurrentSkin from "../../../utils/getCurrentSkin";
import { ThemeContext } from "../../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search, ChevronLeft, ChevronRight, RefreshCcw,
  ArrowLeft, PlusCircle, Send, Package, Pencil, Lock
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────── helpers ───────────────────────
const getTodayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Robust date → ISO yyyy-MM-dd
 * Handles: "2026-03-05T00:00:00.000Z" | "2026-03-05" | "05/03/2026" | "05T00:00:00.000Z/03/2026"
 */
function toISO(v) {
  if (!v) return "";
  const s = String(v).trim();
  // Already clean ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO with time
  if (s.includes("T")) {
    const part = s.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part;
  }
  // dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    return `${yyyy}-${mm}-${dd}`;
  }
  // Fallback: Date parse
  try {
    const d = new Date(s);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  } catch { /* ignore */ }
  return "";
}

/** ISO → dd/MM/yyyy */
function isoToddmm(v) {
  const iso = toISO(v);
  if (!iso) return "";
  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}/${mm}/${yyyy}`;
}

/** Compare dd/MM/yyyy: negative if a < b */
function compareDDMM(a, b) {
  if (!a || !b) return 0;
  return new Date(toISO(a)) - new Date(toISO(b));
}

// ─────────────────────── Pagination ───────────────────────
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  let start = Math.max(1, page - 3);
  let end   = Math.min(totalPages, start + 6);
  if (end - start < 6) start = Math.max(1, end - 6);
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
      <button onClick={() => onPage(page - 1)} disabled={page === 1}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronLeft size={15} />
      </button>
      {start > 1 && (<><button onClick={() => onPage(1)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">1</button><span className="text-xs px-1">…</span></>)}
      {pages.map(p => (
        <button key={p} onClick={() => onPage(p)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${p === page ? "bg-blue-600 text-white" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
          {p}
        </button>
      ))}
      {end < totalPages && (<><span className="text-xs px-1">…</span><button onClick={() => onPage(totalPages)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">{totalPages}</button></>)}
      <button onClick={() => onPage(page + 1)} disabled={page === totalPages}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function STNRequisitionEntry() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const currentSkin = getCurrentSkin();
  const { baseColor, baseShade } = currentSkin;
  const primaryBtnClass   = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session values ──
  const branchId    = Number(localStorage.getItem("branchId")  || 0);
  const branchType  = localStorage.getItem("branchType")       || "HO";
  const loginUserId = Number(localStorage.getItem("userId")    || 0);
  const finYearId   = Number(localStorage.getItem("finYearId") || 0);
  const fy = localStorage.getItem("fyr") || ""; // "2023-2024"

let finYearStart = "";
let finYearEnd = "";

if (fy.includes("-")) {
  const [startYear, endYear] = fy.split("-");

  finYearStart = `01/04/${startYear}`;
  finYearEnd = `31/03/${endYear}`;
}


  // ── mode detection ──
  const stnRequestIdFromSession = sessionStorage.getItem("STNRequestid") || "";
  const isViewMode = Boolean(stnRequestIdFromSession);

  // ── styles ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark ? "bg-gray-800/60 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
            : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");
  const inputDisabled = `w-full px-3 py-2 rounded-xl border text-sm cursor-not-allowed ` +
    (isDark ? "bg-gray-700/40 border-white/10 text-gray-400 opacity-70"
            : "bg-gray-100 border-gray-200 text-gray-500 opacity-80");
  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;
  const cardBase  = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark ? "border-white/10 bg-gray-800/40 backdrop-blur-md" : "border-gray-200 bg-white");
  const thBase = `px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white ${primaryBtnClass}`;
  const tdBase = `px-3 py-2 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`;

  // ── header form state ──
  const [stnReqNo,    setStnReqNo]    = useState("");
  const [date,        setDate]        = useState("");
  const [fromStoreId, setFromStoreId] = useState("Select");
  const [toStoreId,   setToStoreId]   = useState("Select");
  const [deptId,      setDeptId]      = useState("Select");
  const [employeeId,  setEmployeeId]  = useState("Select");
  const [indentRef,   setIndentRef]   = useState("");
  const [remarks,     setRemarks]     = useState("");
  const [chkClose,    setChkClose]    = useState(false);
  const [chkCancel,   setChkCancel]   = useState(false);
  const [existsTransaction, setExistsTransaction] = useState(false);

  // ── dropdown data ──
  const [itemCategories, setItemCategories] = useState([]);
  const [fromStores,     setFromStores]     = useState([]);
  const [toStores,       setToStores]       = useState([]);
  const [departments,    setDepartments]    = useState([]);
  const [employees,      setEmployees]      = useState([]);

  // ── filtration state ──
  const [categoryId, setCategoryId] = useState("Select");
  const [itemCode,   setItemCode]   = useState("");
  const [itemName,   setItemName]   = useState("");

  // ── grid 1 state ──
  const [searchResults,  setSearchResults]  = useState([]);
  const [searchLoading,  setSearchLoading]  = useState(false);
  const [selectedItems,  setSelectedItems]  = useState({});
  const [selectAll,      setSelectAll]      = useState(false);
  const [srchPage,       setSrchPage]       = useState(1);
  const SRCH_PAGE_SIZE   = 10;
  const srchTotalPages   = Math.ceil(searchResults.length / SRCH_PAGE_SIZE);
  const pagedSearchResults = searchResults.slice((srchPage - 1) * SRCH_PAGE_SIZE, srchPage * SRCH_PAGE_SIZE);

  // ── grid 2 state ──
  const [addedItems,   setAddedItems]   = useState([]);
  const [submitChecks, setSubmitChecks] = useState({});

  // ── loading ──
  const [metaLoading,    setMetaLoading]    = useState(false);
  const [submitLoading,  setSubmitLoading]  = useState(false);
  const [viewLoading,    setViewLoading]    = useState(false);
  const [toStoreLoading, setToStoreLoading] = useState(false);

  // ── autocomplete ──
  const [acCodeSuggestions, setAcCodeSuggestions] = useState([]);
  const [acNameSuggestions, setAcNameSuggestions] = useState([]);
  const [showAcCode, setShowAcCode] = useState(false);
  const [showAcName, setShowAcName] = useState(false);
  const acCodeRef = useRef(null);
  const acNameRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (acCodeRef.current && !acCodeRef.current.contains(e.target)) setShowAcCode(false);
      if (acNameRef.current && !acNameRef.current.contains(e.target)) setShowAcName(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── derived mode flags ──
  // isLockedMode: existsTransaction=true  → everything read-only
  // isEditMode:   isViewMode + existsTransaction=false → grid editable + Update button
  const isLockedMode = isViewMode && existsTransaction;
  const isEditMode   = isViewMode && !existsTransaction;

  // ═══════════════════════════════════════════
  // Load meta
  // ═══════════════════════════════════════════
  const loadMeta = useCallback(async (overrideFromStore = null) => {
    setMetaLoading(true);
    try {
      const storeParam = overrideFromStore || (fromStoreId !== "Select" ? fromStoreId : 0);
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/meta`, {
        params: { branchId, branchType, finYearId, fromStoreId: storeParam },
      });
      if (res.data?.success) {
  setItemCategories(res.data.itemCategories || []);
  setFromStores(res.data.fromStores         || []);
  setToStores(res.data.toStores             || []);
  setDepartments(res.data.departments       || []);
  setEmployees(res.data.employees           || []);

  if (res.data.defaultFromStoreId && fromStoreId === "Select") {
    const defaultId = String(res.data.defaultFromStoreId);
    setFromStoreId(defaultId);

    // ✅ FIX: the /meta call above ran with fromStoreId=0 (before the
    // default was known), so toStores came back empty. Re-fetch the
    // To-Store list now that we know the auto-selected From-Store.
    try {
      const toRes = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/to-stores`, {
        params: { fromStoreId: defaultId, finYearId },
      });
      if (toRes.data?.success) setToStores(toRes.data.toStores || []);
    } catch (e) {
      console.error("to-stores (default select):", e.message);
    }
  }
}
    } catch (e) { console.error("meta:", e.message); }
    finally { setMetaLoading(false); }
  }, [branchId, branchType, finYearId, fromStoreId]);

  // ═══════════════════════════════════════════
  // Generate STN number
  // ═══════════════════════════════════════════
  const generateSTNNumber = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/stn-number`, {
        params: { branchId, finYearId },
      });
      if (res.data?.success) setStnReqNo(res.data.stnNumber || "");
    } catch (e) { console.error("stn-number:", e.message); }
  }, [branchId, finYearId]);

  // ═══════════════════════════════════════════
  // Load view/edit details
  // FIXED: robust date parsing for all fields
  // ═══════════════════════════════════════════
  const loadViewDetails = useCallback(async () => {
    if (!stnRequestIdFromSession) return;
    setViewLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/view`, {
        params: { stnRequestId: stnRequestIdFromSession, finYearId },
      });
      if (res.data?.success) {
        const m = res.data.master;
        console.log("records:", m);
        
        if (m) {
          setStnReqNo(m.fld_STNRequestNo || "");
          // ✅ FIX 1: robust date parse — handles ISO with time, dd/MM/yyyy, etc.
          setDate(toISO(m.fld_Date));

          const fStore = String(m.fld_Storeid || "Select");
          setFromStoreId(fStore);
          setToStoreId(String(m.fld_ToStoreid || m.fld_tostoreid || "Select"));
          setDeptId(String(m.fld_Deptid       || "Select"));
          setEmployeeId(String(m.fld_Employeeid || "Select"));
          setIndentRef(m.fld_Indentref || "");
          setRemarks(m.fld_Remarks     || "");

          // Populate to-stores dropdown for this from-store
          if (fStore && fStore !== "Select") {
            try {
              const rs = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/to-stores`, {
                params: { fromStoreId: fStore, finYearId },
              });
              if (rs.data?.success) setToStores(rs.data.toStores || []);
            } catch { /* silent */ }
          }
        }

        // ✅ KEY: existsTransaction drives locked vs editable
        const hasTransaction = res.data.existsTransaction || false;
        setExistsTransaction(hasTransaction);

        const mapped = (res.data.details || []).map((d) => ({
          itemId:      d.fld_itemid    || d.itemid    || "",
          itemcode:    d.fld_itemcode  || d.itemcode  || "",
          itemname:    d.fld_itemname  || d.itemname  || "",
          itemPart:    d.fld_itemPartNo || d.itemPart || "",
          itemuom:     d.fld_itemuom   || d.itemuom   || "",
          qty:         String(d.fld_qty || ""),
          // ✅ FIX 2: dueDate from DB → always dd/MM/yyyy for display/validation
          dueDate:     isoToddmm(toISO(d.fld_duedate || d.fld_DueDate || "")),
          itemRemarks: d.fld_remarks   || "",
        }));
        setAddedItems(mapped);

        // Pre-check all rows when edit mode, none when locked
        const checks = {};
        mapped.forEach((_, i) => { checks[i] = !hasTransaction; });
        setSubmitChecks(checks);
      }
    } catch (e) {
      toast.error("Failed to load STN details: " + e.message);
    } finally {
      setViewLoading(false);
    }
  }, [stnRequestIdFromSession, finYearId]);

  // ── initial load ──
  useEffect(() => {
    loadMeta();
    if (isViewMode) {
      loadViewDetails();
} else {
  generateSTNNumber();
  // 🚧 TEMP DISABLED: FY clamp on default date, same reason as validateDate().
  // const todayISO = getTodayISO();
  // const startISO = finYearStart ? toISO(finYearStart) : "";
  // const endISO   = finYearEnd   ? toISO(finYearEnd)   : "";
  // let defaultDate = todayISO;
  // if (endISO && todayISO > endISO)   defaultDate = endISO;
  // else if (startISO && todayISO < startISO) defaultDate = startISO;
  setDate(getTodayISO());
}
  }, []); // eslint-disable-line

  // ═══════════════════════════════════════════
  // From-store change
  // ═══════════════════════════════════════════
  const handleFromStoreChange = useCallback(async (val) => {
    setFromStoreId(val);
    setToStoreId("Select");
    setToStoreLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/to-stores`, {
        params: { fromStoreId: val, finYearId },
      });
      if (res.data?.success) setToStores(res.data.toStores || []);
    } catch { /* silent */ }
    finally { setToStoreLoading(false); }
  }, [finYearId]);

  // ═══════════════════════════════════════════
  // Dept change
  // ═══════════════════════════════════════════
  const handleDeptChange = useCallback(async (val) => {
    setDeptId(val);
    setEmployeeId("Select");
    if (val === "Select") { setEmployees([]); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/employees-by-dept`, {
        params: { deptId: val, branchId, finYearId },
      });
      if (res.data?.success) setEmployees(res.data.employees || []);
    } catch { /* silent */ }
  }, [branchId, finYearId]);

  // ═══════════════════════════════════════════
  // Autocomplete
  // ═══════════════════════════════════════════
  const fetchAcCode = useCallback(async (prefix) => {
    if (!prefix) { setAcCodeSuggestions([]); setShowAcCode(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/autocomplete`, {
        params: { prefix, type: "Code", toStoreId, fromStoreId, categoryId, itemCode, itemName, finYearId },
      });
      if (res.data?.success) { setAcCodeSuggestions(res.data.suggestions || []); setShowAcCode(true); }
    } catch { /* silent */ }
  }, [toStoreId, fromStoreId, categoryId, itemCode, itemName, finYearId]);

  const fetchAcName = useCallback(async (prefix) => {
    if (!prefix) { setAcNameSuggestions([]); setShowAcName(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/autocomplete`, {
        params: { prefix, type: "Name", toStoreId, fromStoreId, categoryId, itemCode, itemName, finYearId },
      });
      if (res.data?.success) { setAcNameSuggestions(res.data.suggestions || []); setShowAcName(true); }
    } catch { /* silent */ }
  }, [toStoreId, fromStoreId, categoryId, itemCode, itemName, finYearId]);

  // ═══════════════════════════════════════════
  // Validations
  // ═══════════════════════════════════════════
function validateDate(isoVal) {
  if (!isoVal) { toast.warning("Please select Date"); return false; }
  const sel   = new Date(isoVal);
  const today = new Date(getTodayISO());
  if (sel > today) { toast.warning("Please select Date Less than or Equal to Current date"); return false; }

  // 🚧 TEMP DISABLED: Financial Year range check — session finYearStart/finYearEnd
  // data is unreliable right now (e.g. stale FY vs system date). Re-enable once
  // the FY session values are fixed to reflect the actual active year.
  // if (finYearStart) {
  //   if (sel < new Date(toISO(finYearStart))) { toast.warning(`Date must be ≥ Financial Year start (${finYearStart})`); return false; }
  // }
  // if (finYearEnd) {
  //   if (sel > new Date(toISO(finYearEnd))) { toast.warning(`Date must be ≤ Financial Year end (${finYearEnd})`); return false; }
  // }

  return true;
}

  function validateHeader() {
    if (!date) { toast.warning("Please select Date"); return false; }
    if (!validateDate(date)) return false;
    if (!fromStoreId || fromStoreId === "Select") { toast.warning("Please select Requesting Store"); return false; }
    if (!toStoreId   || toStoreId   === "Select") { toast.warning("Please select Requested Store"); return false; }
    if (!deptId      || deptId      === "Select") { toast.warning("Please select Department"); return false; }
    return true;
  }

  function validateGrid() {
    const checked = addedItems.filter((_, i) => submitChecks[i]);
    if (checked.length === 0) { toast.warning("Please select at least one item"); return false; }
    for (let i = 0; i < addedItems.length; i++) {
      if (!submitChecks[i]) continue;
      const item = addedItems[i];
      if (!item.qty || Number(item.qty) < 1) { toast.warning(`Please enter valid quantity for row ${i + 1}`); return false; }
      if (!item.dueDate) { toast.warning(`Please select Due Date for row ${i + 1}`); return false; }
      if (compareDDMM(item.dueDate, isoToddmm(date)) < 0) {
        toast.warning(`Due date must be ≥ requisition date (row ${i + 1})`); return false;
      }
    }
    return true;
  }

  // ═══════════════════════════════════════════
  // Search items
  // ═══════════════════════════════════════════
  const handleSearch = useCallback(async () => {
    if (toStoreId === "Select") { toast.warning("Please Select the Requested Store"); return; }
    setSearchLoading(true); setSrchPage(1); setSelectedItems({}); setSelectAll(false);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-entry/search-items`, {
        params: { toStoreId, fromStoreId, categoryId, itemCode, itemName, finYearId },
      });
      if (res.data?.success) {
        setSearchResults(res.data.items || []);
        if ((res.data.items || []).length === 0) toast.info("No records found");
      }
    } catch (e) { toast.error("Search failed: " + (e?.response?.data?.message || e.message)); }
    finally { setSearchLoading(false); }
  }, [toStoreId, fromStoreId, categoryId, itemCode, itemName, finYearId]);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const nc = {};
    pagedSearchResults.forEach((_, li) => { nc[(srchPage - 1) * SRCH_PAGE_SIZE + li] = checked; });
    setSelectedItems(prev => ({ ...prev, ...nc }));
  };

  // ═══════════════════════════════════════════
  // Add items to grid 2
  // ═══════════════════════════════════════════
  const handleAddItems = () => {
    const checked = searchResults.filter((_, i) => selectedItems[i]);
    if (!checked.length) { toast.warning("Please select at least one item"); return; }
    setAddedItems(prev => {
      const existingIds = new Set(prev.map(i => String(i.itemId)));
      const newItems = checked
        .filter(item => !existingIds.has(String(item.itemid)))
        .map(item => ({
          itemId:      item.itemid,
          itemcode:    item.itemcode    || "",
          itemname:    item.itemname    || "",
          itemPart:    item.itemPart    || "",
          itemuom:     item.itemuom     || "",
          qty:         item.fld_qty     || "",
          dueDate:     item.fld_duedate || "",
          itemRemarks: item.fld_remarks || "",
        }));
      const dups = checked.length - newItems.length;
      if (dups > 0) toast.info(`${dups} duplicate(s) skipped`);
      if (newItems.length > 0) toast.success(`${newItems.length} item(s) added`);
      return [...prev, ...newItems];
    });
    setSelectedItems({}); setSelectAll(false);
  };

  const updateAddedItem = (idx, field, value) =>
    setAddedItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));

  // ═══════════════════════════════════════════
  // Submit / Update
  // ═══════════════════════════════════════════
  const handleSubmit = useCallback(async () => {
    if (!validateHeader()) return;
    if (!validateGrid())   return;

    const items = addedItems
      .filter((_, i) => submitChecks[i])
      .map(item => ({ itemId: item.itemId, qty: item.qty, dueDate: item.dueDate, itemRemarks: item.itemRemarks || "" }));

    setSubmitLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/stn-requisition-entry/submit`, {
        branchId, branchType, finYearId, loginUserId, stnReqNo,
        date: isoToddmm(date), fromStoreId, toStoreId, deptId,
        employeeId: employeeId !== "Select" ? employeeId : null,
        indentRef, remarks, items,
      });
      if (res.data?.success) {
        toast.success(isEditMode ? "STN Requisition updated!" : "STN Requisition saved!");
        sessionStorage.removeItem("STNRequestid");
        setTimeout(() => navigate("/Inventory/STNRequisition_Listing"), 1500);
      } else {
        toast.error(res.data?.message || "Submission failed");
      }
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSubmitLoading(false);
    }
  }, [addedItems, submitChecks, branchId, branchType, finYearId, loginUserId,
      stnReqNo, date, fromStoreId, toStoreId, deptId, employeeId,
      indentRef, remarks, isEditMode, navigate]);

  const handleBack = () => { sessionStorage.removeItem("STNRequestid"); navigate("/Inventory/STNRequisition_Listing"); };
  const handleChkClose  = (c) => { setChkClose(c);  if (c) setChkCancel(false); };
  const handleChkCancel = (c) => { setChkCancel(c); if (c) setChkClose(false);  };

  // ─────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar title="Stock Transfer Requisition" />
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen px-4 py-6 ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>

        {/* ══ HEADER CARD ══ */}
        <div className={`${cardBase} mb-6`}>
          <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
            <div className="flex items-center gap-3">
              <Package size={18} className="text-white" />
              <h2 className="text-sm font-bold text-white tracking-wider uppercase">Stock Transfer Requisition</h2>
              {isLockedMode && (
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Lock size={10} /> Read Only
                </span>
              )}
              {isEditMode && (
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Pencil size={10} /> Edit Mode
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/70 text-xs font-medium">INFRA SCREEN : A-25/1</span>
              <button onClick={handleBack}
                className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition">
                <ArrowLeft size={13} /> Back
              </button>
            </div>
          </div>

          <div className="p-5">
            {viewLoading ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCcw size={20} className="animate-spin text-blue-500 mr-2" />
                <span className="text-sm text-gray-500">Loading details...</span>
              </div>
            ) : (
              <>
                {/* Mode banner */}
                {isViewMode && (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold mb-4
                    ${isLockedMode
                      ? (isDark ? "bg-gray-700/60 text-gray-300 border border-white/10" : "bg-gray-100 text-gray-600 border border-gray-200")
                      : (isDark ? "bg-blue-900/40 text-blue-300 border border-blue-700/40" : "bg-blue-50 text-blue-700 border border-blue-200")}`}>
                    {isLockedMode
                      ? <><Lock size={13} /> This requisition has downstream transactions — <strong>read-only</strong>.</>
                      : <><Pencil size={13} /> No downstream transactions — you can <strong>update</strong> this requisition.</>}
                  </div>
                )}

                <div className={`text-xs font-bold uppercase tracking-widest mb-4 pb-1 border-b
                  ${isDark ? "text-gray-400 border-white/10" : "text-gray-400 border-gray-200"}`}>
                  Header Area
                </div>

                {/* Row 1: STN Req No + Department */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelBase}>STN Req Number</label>
                    <div className={`mt-1 px-3 py-2 rounded-xl border text-sm font-bold
                      ${isDark ? "bg-gray-700/60 border-white/10 text-purple-300" : "bg-purple-50 border-purple-200 text-purple-700"}`}>
                      {stnReqNo || <span className="opacity-40 font-normal text-xs">Generating...</span>}
                    </div>
                  </div>
                  <div>
                    <label className={labelBase}>Department <span className="text-red-500">*</span></label>
                    {isViewMode ? (
                      <div className={`mt-1 ${inputDisabled}`}>
                        {departments.find(d => String(d.fld_deptid) === String(deptId))?.fld_departmentname || deptId || "—"}
                      </div>
                    ) : (
                      <select value={deptId} onChange={e => handleDeptChange(e.target.value)} className={inputBase + " mt-1"}>
                        <option value="Select">Select</option>
                        {departments.map(d => <option key={d.fld_deptid} value={d.fld_deptid}>{d.fld_departmentname}</option>)}
                      </select>
                    )}
                  </div>
                </div>

                {/* Row 2: Date + Employee */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelBase}>Date <span className="text-red-500">*</span></label>
                    {isViewMode ? (
                      /* ✅ FIX: display dd/MM/yyyy, never raw ISO */
                      <div className={`mt-1 ${inputDisabled}`}>{isoToddmm(date) || "—"}</div>
                    ) : (
                      <input type="date" value={date}
  // 🚧 TEMP DISABLED: FY-based max/min, same reason as validateDate().
  max={getTodayISO()}
  min=""
  onChange={e => { setDate(e.target.value); validateDate(e.target.value); }}
  className={inputBase + " mt-1"} />
                    )}
                  </div>
                  <div>
                    <label className={labelBase}>Employee</label>
                    {isViewMode ? (
                      <div className={`mt-1 ${inputDisabled}`}>
                        {employees.find(e => String(e.fld_Employeeid) === String(employeeId))?.fld_Employee || employeeId || "—"}
                      </div>
                    ) : (
                      <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} className={inputBase + " mt-1"}>
                        <option value="Select">Select</option>
                        {employees.map(e => <option key={e.fld_Employeeid} value={e.fld_Employeeid}>{e.fld_Employee}</option>)}
                      </select>
                    )}
                  </div>
                </div>

                {/* Row 3: Requesting Store + Indent Ref */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelBase}>Requesting Store <span className="text-red-500">*</span></label>
                    {isViewMode ? (
                      <div className={`mt-1 ${inputDisabled}`}>
                        {fromStores.find(s => String(s.fld_storeid) === String(fromStoreId))?.fld_storename || fromStoreId || "—"}
                      </div>
                    ) : (
                      <select value={fromStoreId} onChange={e => handleFromStoreChange(e.target.value)} className={inputBase + " mt-1"}>
                        <option value="Select">Select</option>
                        {fromStores.map(s => <option key={s.fld_storeid} value={s.fld_storeid}>{s.fld_storename}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className={labelBase}>Indent Ref</label>
                    {isViewMode ? (
                      <div className={`mt-1 ${inputDisabled}`}>{indentRef || "—"}</div>
                    ) : (
                      <input value={indentRef} onChange={e => setIndentRef(e.target.value)}
                        placeholder="Enter indent reference" className={inputBase + " mt-1"} />
                    )}
                  </div>
                </div>

                {/* Row 4: Requested Store + Remarks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelBase}>Requested Store <span className="text-red-500">*</span></label>
                    {isViewMode ? (
                      <div className={`mt-1 ${inputDisabled}`}>
                        {toStores.find(s => String(s.fld_storeid) === String(toStoreId))?.fld_storename || toStoreId || "—"}
                      </div>
                    ) : (
                      <select value={toStoreId} onChange={e => setToStoreId(e.target.value)}
                        disabled={toStoreLoading} className={inputBase + " mt-1"}>
                        <option value="Select">Select</option>
                        {toStores.map(s => <option key={s.fld_storeid} value={s.fld_storeid}>{s.fld_storename}</option>)}
                      </select>
                    )}
                    {toStoreLoading && <p className="text-xs text-blue-500 mt-1">Loading stores...</p>}
                  </div>
                  <div>
                    <label className={labelBase}>Remarks</label>
                    {isViewMode ? (
                      <div className={`mt-1 ${inputDisabled} min-h-[52px]`}>{remarks || "—"}</div>
                    ) : (
                      <textarea value={remarks} onChange={e => setRemarks(e.target.value)}
                        rows={2} placeholder="Enter remarks" className={inputBase + " mt-1 resize-none"} />
                    )}
                  </div>
                </div>

                {/* Row 5: Close + Cancel (view only) */}
                {isViewMode && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                    <div className="flex items-center gap-3">
                      <label className={labelBase}>Close</label>
                      <input type="checkbox" checked={chkClose}
                        onChange={e => handleChkClose(e.target.checked)} disabled={chkCancel}
                        className="w-4 h-4 accent-blue-600 disabled:opacity-40 cursor-pointer" />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className={labelBase}>Cancel</label>
                      <input type="checkbox" checked={chkCancel}
                        onChange={e => handleChkCancel(e.target.checked)} disabled={chkClose}
                        className="w-4 h-4 accent-red-600 disabled:opacity-40 cursor-pointer" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ══ FILTRATION AREA (new entry only) ══ */}
        {!isViewMode && (
          <div className={`${cardBase} mb-6`}>
            <div className={`px-5 py-3 ${primaryBtnClass}`}>
              <h2 className="text-sm font-bold text-white tracking-wider uppercase">Filtration Area</h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelBase}>Item Category</label>
                  <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className={inputBase + " mt-1"}>
                    <option value="Select">Select</option>
                    {itemCategories.map(c => <option key={c.fld_itemcategoryid} value={c.fld_itemcategoryid}>{c.fld_itemcategory_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelBase}>Item Code</label>
                  <div className="relative mt-1" ref={acCodeRef}>
                    <input value={itemCode}
                      onChange={e => { setItemCode(e.target.value); fetchAcCode(e.target.value); }}
                      onFocus={() => itemCode && setShowAcCode(acCodeSuggestions.length > 0)}
                      placeholder="Type item code..." className={inputBase} />
                    {showAcCode && acCodeSuggestions.length > 0 && (
                      <div className={`absolute z-30 left-0 right-0 mt-1 rounded-xl border shadow-xl max-h-48 overflow-y-auto
                        ${isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200"}`}>
                        {acCodeSuggestions.map((s, i) => (
                          <div key={i} onClick={() => { setItemCode(s); setShowAcCode(false); }}
                            className={`px-3 py-2 text-sm cursor-pointer ${isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}>
                            {s}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelBase}>Item Name</label>
                  <div className="relative mt-1" ref={acNameRef}>
                    <input value={itemName}
                      onChange={e => { setItemName(e.target.value); fetchAcName(e.target.value); }}
                      onFocus={() => itemName && setShowAcName(acNameSuggestions.length > 0)}
                      placeholder="Type item name..." className={inputBase} />
                    {showAcName && acNameSuggestions.length > 0 && (
                      <div className={`absolute z-30 left-0 right-0 mt-1 rounded-xl border shadow-xl max-h-48 overflow-y-auto
                        ${isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200"}`}>
                        {acNameSuggestions.map((s, i) => (
                          <div key={i} onClick={() => { setItemName(s); setShowAcName(false); }}
                            className={`px-3 py-2 text-sm cursor-pointer ${isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}>
                            {s}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-center pt-1">
                <button onClick={handleSearch} disabled={searchLoading}
                  className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}>
                  {searchLoading ? <RefreshCcw size={15} className="animate-spin" /> : <Search size={15} />}
                  {searchLoading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ GRID 1 — Search Results (new entry only) ══ */}
        {!isViewMode && (
          <div className={`${cardBase} mb-6`}>
            <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
              <h2 className="text-sm font-bold text-white tracking-wider uppercase">
                Item Search Results
                {searchResults.length > 0 && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">{searchResults.length}</span>}
              </h2>
              {searchResults.length > 0 && (
                <button onClick={handleAddItems}
                  className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition">
                  <PlusCircle size={13} /> Add Selected
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thBase + " text-center"}>
                      <input type="checkbox" checked={selectAll} onChange={e => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 accent-white cursor-pointer" />
                    </th>
                    <th className={thBase}>S.No</th>
                    <th className={thBase}>Item Code</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>UOM</th>
                  </tr>
                </thead>
                <tbody>
                  {searchLoading ? (
                    <tr><td colSpan={5} className={`${tdBase} text-center py-10`}>
                      <RefreshCcw size={20} className="animate-spin inline mr-2 text-blue-500" />Searching...
                    </td></tr>
                  ) : pagedSearchResults.length === 0 ? (
                    <tr><td colSpan={5} className={`${tdBase} text-center py-8 text-gray-400`}>
                      Use filters above and click Search to find items
                    </td></tr>
                  ) : pagedSearchResults.map((item, li) => {
                    const gi = (srchPage - 1) * SRCH_PAGE_SIZE + li;
                    const isChecked = selectedItems[gi] || false;
                    return (
                      <tr key={gi} onClick={() => setSelectedItems(prev => ({ ...prev, [gi]: !isChecked }))}
                        className={`border-t cursor-pointer transition
                          ${isDark ? "border-white/5" : "border-gray-100"}
                          ${isChecked ? (isDark ? "bg-blue-900/30" : "bg-blue-50")
                            : (gi % 2 === 0 ? (isDark ? "bg-gray-800/20 hover:bg-white/5" : "bg-gray-50/60 hover:bg-blue-50/40")
                                           : (isDark ? "hover:bg-white/5" : "hover:bg-blue-50/40"))}`}>
                        <td className={`${tdBase} text-center`}>
                          <input type="checkbox" checked={isChecked}
                            onChange={e => { e.stopPropagation(); setSelectedItems(prev => ({ ...prev, [gi]: !isChecked })); }}
                            className="w-4 h-4 accent-blue-600 cursor-pointer" />
                        </td>
                        <td className={`${tdBase} text-center`}>{gi + 1}</td>
                        <td className={tdBase}>{item.itemcode}</td>
                        <td className={tdBase}>{item.itemname}</td>
                        <td className={tdBase}>{item.itemuom}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {searchResults.length > 0 && (
              <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2
                ${isDark ? "border-white/10 text-gray-400" : "border-gray-100 text-gray-500"}`}>
                <span>Showing {Math.min((srchPage - 1) * SRCH_PAGE_SIZE + 1, searchResults.length)}–{Math.min(srchPage * SRCH_PAGE_SIZE, searchResults.length)} of {searchResults.length}</span>
                <Pagination page={srchPage} totalPages={srchTotalPages} onPage={p => { setSrchPage(p); setSelectAll(false); }} />
              </div>
            )}
          </div>
        )}

        {/* ══ GRID 2 — Items (all modes) ══ */}
        {(addedItems.length > 0 || isViewMode) && (
          <div className={cardBase}>
            <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
              <h2 className="text-sm font-bold text-white tracking-wider uppercase">
                {isViewMode ? "Requisition Items" : "Items to Submit"}
                {addedItems.length > 0 && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">{addedItems.length}</span>}
              </h2>
              {/* ✅ Update button — isEditMode (view + no transaction) */}
              {/* {isEditMode && addedItems.length > 0 && (
                <button onClick={handleSubmit} disabled={submitLoading}
                  className="flex items-center gap-1.5 bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-60">
                  {submitLoading ? <><RefreshCcw size={12} className="animate-spin" /> Updating...</> : <><Pencil size={12} /> Update</>}
                </button>
              )} */}
              {/* Submit button — new entry */}
              {!isViewMode && addedItems.length > 0 && (
                <button onClick={handleSubmit} disabled={submitLoading}
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-60">
                  {submitLoading ? <><RefreshCcw size={12} className="animate-spin" /> Submitting...</> : <><Send size={12} /> Submit</>}
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${thBase} text-center`}>Select</th>
                    <th className={thBase}>S.No</th>
                    <th className={thBase}>Item Code</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>Qty</th>
                    <th className={thBase}>Due Date</th>
                    <th className={thBase}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {addedItems.length === 0 ? (
                    <tr><td colSpan={7} className={`${tdBase} text-center py-8 text-gray-400`}>No Records Found</td></tr>
                  ) : addedItems.map((item, idx) => {
                    // isLockedMode → all fields disabled, isEditMode → all enabled, new → all enabled
                    const fieldsLocked = isLockedMode;
                    const isChecked    = submitChecks[idx] || false;
                    return (
                      <tr key={idx}
                        className={`border-t transition ${isDark ? "border-white/5" : "border-gray-100"}
                          ${idx % 2 === 0 ? (isDark ? "bg-gray-800/20" : "bg-gray-50/60") : ""}`}>

                        <td className={`${tdBase} text-center`}>
                          <input type="checkbox" checked={isChecked} disabled={fieldsLocked}
                            onChange={() => !fieldsLocked && setSubmitChecks(prev => ({ ...prev, [idx]: !isChecked }))}
                            className={`w-4 h-4 accent-blue-600 ${fieldsLocked ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`} />
                        </td>
                        <td className={`${tdBase} text-center`}>{idx + 1}</td>
                        <td className={tdBase}>{item.itemcode}</td>
                        <td className={tdBase}>{item.itemname}</td>

                        {/* Qty */}
                        <td className={tdBase}>
                          {fieldsLocked ? (
                            <span className="opacity-70">{item.qty || "—"}</span>
                          ) : (
                            <input type="number" min="1" value={item.qty}
                              onChange={e => updateAddedItem(idx, "qty", e.target.value)}
                              onBlur={e => { if (Number(e.target.value) < 1) { toast.warning("Please enter valid number"); updateAddedItem(idx, "qty", ""); } }}
                              className={`w-24 px-2 py-1 rounded-lg border text-xs outline-none
                                ${isDark ? "bg-gray-700 border-white/10 text-white focus:ring-1 focus:ring-blue-500"
                                         : "bg-white border-gray-300 text-gray-800 focus:ring-1 focus:ring-blue-400"}`} />
                          )}
                        </td>

                        {/* Due Date — ✅ FIX: toISO converts dd/MM/yyyy → yyyy-MM-dd for input */}
                        <td className={tdBase}>
                          {fieldsLocked ? (
                            <span className="opacity-70">{item.dueDate || "—"}</span>
                          ) : (
                            <input type="date"
                              value={toISO(item.dueDate)}
                              min={date}
                              onChange={e => updateAddedItem(idx, "dueDate", isoToddmm(e.target.value))}
                              className={`w-36 px-2 py-1 rounded-lg border text-xs outline-none
                                ${isDark ? "bg-gray-700 border-white/10 text-white focus:ring-1 focus:ring-blue-500"
                                         : "bg-white border-gray-300 text-gray-800 focus:ring-1 focus:ring-blue-400"}`} />
                          )}
                        </td>

                        {/* Remarks */}
                        <td className={tdBase}>
                          {fieldsLocked ? (
                            <span className="opacity-70">{item.itemRemarks || "—"}</span>
                          ) : (
                            <textarea value={item.itemRemarks} rows={1}
                              onChange={e => updateAddedItem(idx, "itemRemarks", e.target.value)}
                              className={`w-28 px-2 py-1 rounded-lg border text-xs outline-none resize-none
                                ${isDark ? "bg-gray-700 border-white/10 text-white focus:ring-1 focus:ring-blue-500"
                                         : "bg-white border-gray-300 text-gray-800 focus:ring-1 focus:ring-blue-400"}`} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer submit/update button */}
            {/* {(isEditMode || !isViewMode) && addedItems.length > 0 && (
              <div className={`px-5 py-3 border-t flex justify-end ${isDark ? "border-white/10" : "border-gray-100"}`}>
                <button onClick={handleSubmit} disabled={submitLoading}
                  className={`flex items-center gap-2 text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60
                    ${isEditMode ? "bg-yellow-500 hover:bg-yellow-600" : "bg-green-600 hover:bg-green-700"}`}>
                  {submitLoading
                    ? <><RefreshCcw size={15} className="animate-spin" /> {isEditMode ? "Updating..." : "Submitting..."}</>
                    : <><Send size={15} /> {isEditMode ? "Update Requisition" : "Submit Requisition"}</>}
                </button>
              </div>
            )} */}
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
}